<template>
  <el-dialog
    v-model="isOpen"
    title="Flat deal request form"
    width="75%"
    center
    :show-close="false"
    :close-on-press-escape="false"
    :close-on-click-modal="false"
  >
    <el-form
      ref="formObj"
      v-loading="loading"
      :disabled="loading"
      :rules="rules"
      :model="formData"
      label-position="top"
    >
      <el-container>
        <el-header
          >Specify the traffic you want to have by using the filters and leave a
          message to the publisher.</el-header
        >
        <el-container>
          <el-main>
            <el-row :gutter="20">
              <el-col :span="8" :xs="24">
                <el-form-item label="Website domain" prop="domains">
                  <RefHostSelect v-model="formData.domains" />
                </el-form-item>
              </el-col>
              <el-col :span="8" :xs="24">
                <el-form-item label="Ad type" prop="type_id">
                  <AdTypeSelect
                    v-model="formData.type_id"
                    :radio-view="false"
                    :with-empty="false"
                  />
                </el-form-item>
              </el-col>
              <el-col :span="8" :xs="24">
                <el-form-item label="Size">
                  <SizeSelect
                    v-model="formData.size_id"
                    :disabled="formData.type_id !== AdType.Banner"
                  />
                </el-form-item>
              </el-col>
            </el-row>
            <el-row :gutter="20">
              <el-col :span="8" :xs="24">
                <el-form-item label="Country" prop="countries_ids">
                  <CountrySelect
                    v-model="formData.countries_ids"
                    :items="countries"
                    :multiple="true"
                    :use-id="true"
                  />
                </el-form-item>
              </el-col>
              <el-col :span="8" :xs="24">
                <el-form-item label="Stream" prop="stream">
                  <StreamSelect v-model="formData.stream" />
                </el-form-item>
              </el-col>
              <el-col :span="8" :xs="24">
                <el-form-item prop="device_ids">
                  <DeviceSelector v-model="formData.device_ids" />
                </el-form-item>
              </el-col>
            </el-row>
            <el-row :gutter="20">
              <el-col :span="8" :xs="24">
                <el-form-item label="Duration (days)" prop="duration">
                  <el-input
                    v-model="formData.duration"
                    placeholder="Duration"
                  />
                </el-form-item>
              </el-col>
              <el-col :span="8" :xs="24">
                <el-form-item label="Deal Type" prop="deal_types">
                  <FlatDealTypeSelect v-model="formData.deal_types" />
                </el-form-item>
              </el-col>
              <el-col :span="8" :xs="24">
                <el-form-item label="Estimated impressions/day" prop="hits">
                  <el-input
                    v-model="formData.hits"
                    placeholder="Estimated hits per day"
                    type="number"
                  />
                </el-form-item>
              </el-col>
            </el-row>
            <el-row>
              <el-col :span="24" :xs="24">
                <el-form-item label="Message" prop="text">
                  <el-input
                    v-model="formData.text"
                    :autosize="{ minRows: 2, maxRows: 4 }"
                    type="textarea"
                    placeholder="Message text"
                  />
                </el-form-item>
              </el-col>
            </el-row>
          </el-main>
          <el-aside>
            <img class="logo" :src="img" alt="logo" />
            <a
              class="el-button deal-button el-button--primary"
              target="_blank"
              href="https://support.traforama.com/en/articles/flat-deals"
            >
              Read more about Flat deals
            </a>
          </el-aside>
        </el-container>
      </el-container>
    </el-form>
    <template #footer>
      <span class="dialog-footer">
        <el-button @click="close">Cancel</el-button>
        <el-button :disabled="loading" type="primary" @click="submit">
          Confirm
        </el-button>
      </span>
    </template>
  </el-dialog>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch } from 'vue'
import { AdType } from '@/api/types'
import { useMarketplaceStore } from '@/apps/traforama/store/marketplace'
import { ElMessage } from 'element-plus'

import AdTypeSelect from '@/components/Select/AdTypeSelect.vue'
import SizeSelect from '@/components/Select/SizeSelect.vue'
import StreamSelect from '@/components/Select/StreamSelect.vue'
import DeviceSelector from '@/apps/traforama/components/DeviceSelector.vue'
import CountrySelect from '@/components/Select/CountrySelect.vue'
import FlatDealTypeSelect from '@/components/Select/FlatDealTypeSelect.vue'
import RefHostSelect from '@/components/Select/RefHostSelect.vue'

import { useCountriesStore } from '@/store/modules/countries'
import img from '@/apps/traforama/assets/icons/dash/deal_request_img.png'

interface Form {
  domains: string[]
  device_ids: string[]
  countries_ids: string[]
  id?: string | null
  type_id?: string
  size_id?: string
  text?: string
  duration?: string
  deal_types?: string
  hits?: string
  pub_types?: string
  stream?: string
}

export default defineComponent({
  name: 'RequestDealModal',
  components: {
    AdTypeSelect,
    StreamSelect,
    DeviceSelector,
    CountrySelect,
    RefHostSelect,
    FlatDealTypeSelect,
    SizeSelect
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    id: {
      type: String,
      default: null
    },
    fields: {
      type: Object,
      default: () => {}
    }
  },
  setup(props, context) {
    const marketplaceStore = useMarketplaceStore()
    const countriesStore = useCountriesStore()
    const isOpen = computed({
      get: () => props.visible,
      set: (val: boolean) => {
        if (!val) {
          context.emit('close')
        }
      }
    })

    const close = () => {
      formObj.value?.clearValidate()
      context.emit('close')
    }
    const loading = computed(() => marketplaceStore.listLoading)
    const formObj = ref()

    const defaultData: Form = {
      domains: [],
      device_ids: [],
      countries_ids: [],
      id: null,
      stream: '0'
    }
    const formData = ref({ ...defaultData })

    const countries = computed(() => {
      return countriesStore.sorted
    })

    watch(props, ({ id }) => {
      if (!id) {
        formData.value = { ...defaultData }
      } else {
        formData.value = { ...defaultData, ...props.fields, id: id }
        if (props.fields.domain) {
          formData.value.domains = [props.fields.domain]
        }
      }
      formObj.value?.clearValidate()
    })

    const submit = () => {
      formObj.value.validate(async (valid: boolean) => {
        if (valid) {
          marketplaceStore.SendMessage(formData.value).then(() => {
            close()
            formData.value = { ...defaultData }
            ElMessage.success({
              message: 'Message was sent successfully',
              type: 'success',
              showClose: true,
              duration: 5000
            })
          })
        } else {
          return false
        }
      })
    }

    const rules = {
      domains: {
        required: true,
        message: 'This field is required',
        trigger: 'change'
      },
      type_id: {
        required: true,
        message: 'This field is required',
        trigger: 'change'
      },
      duration: {
        required: true,
        message: 'This field is required',
        trigger: 'change'
      },
      deal_types: {
        required: true,
        message: 'This field is required',
        trigger: 'change'
      },
      countries_ids: {
        required: true,
        type: 'array',
        min: 1,
        message: 'This field is required',
        trigger: 'change'
      },
      device_ids: {
        required: true,
        type: 'array',
        min: 1,
        message: 'This field is required',
        trigger: 'change'
      },
      stream: {
        required: true,
        message: 'This field is required',
        trigger: 'change'
      },
      hits: {
        required: true,
        message: 'This field is required',
        trigger: 'change'
      }
    }
    return {
      isOpen,
      submit,
      rules,
      close,
      loading,
      formData,
      AdType,
      countries,
      img,
      formObj
    }
  }
})
</script>

<style lang="scss" scoped>
.logo {
  width: 100%;
}

.deal-button {
  width: 100%;
}

.el-select {
  width: 100%;
}

.el-aside {
  width: 30%;
}

.el-header {
  height: 15px;
  font-size: 15px;
}

@media (max-width: 767px) {
  .el-aside {
    width: 100% !important;
  }

  .el-container {
    flex-direction: column-reverse;
  }
}
</style>
